import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import ReusableTrendsGraph from '../common/ReusableTrendsGraph';

const CardContainer = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1.75rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const CardTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
`;

const GraphContainer = styled.div`
  width: 100%;
`;

const PerformanceSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 40px;
  gap: 1rem;
  padding: 0.75rem 1rem;
  background: #f9fafb;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.025em;
`;

const HeaderCell = styled.div`
  text-align: ${props => props.align || 'left'};
`;

const TabGroup = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const Tab = styled.button`
  padding: 0.35rem 0.75rem;
  font-size: 0.8rem;
  font-weight: ${props => props.active ? '600' : '500'};
  color: ${props => props.active ? '#0066FF' : '#6b7280'};
  background: ${props => props.active ? '#EBF5FF' : 'transparent'};
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.active ? '#EBF5FF' : '#f3f4f6'};
  }
`;

const TableBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 40px;
  gap: 1rem;
  align-items: center;
  padding: 1rem;
  border-bottom: 1px solid #f3f4f6;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.15s;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #f9fafb;
  }
  
  & > * {
    min-width: 0;
  }
`;

const ItemName = styled.div`
  font-weight: 600;
  color: #111827;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
  font-size: 0.875rem;
`;

const ItemSubtext = styled.div`
  font-size: 0.75rem;
  color: #9ca3af;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
  margin-top: 0.25rem;
`;

const MetricCell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.25rem;
`;

const MetricValue = styled.div`
  font-weight: 600;
  color: #111827;
  font-size: 0.875rem;
`;

const ProgressBar = styled.div`
  width: 60px;
  height: 4px;
  background: #e5e7eb;
  border-radius: 2px;
  overflow: hidden;
  position: relative;
`;

const ProgressFill = styled.div`
  height: 100%;
  width: ${props => props.value || 0}%;
  background: ${props => 
    props.value >= 95 ? '#22C55E' : 
    props.value >= 80 ? '#F59E0B' : 
    '#EF4444'
  };
  transition: width 0.3s ease;
`;

const StatusBadge = styled.span`
  padding: 0.25rem 0.625rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 500;
  background: ${props => 
    props.status === 'Healthy' || props.status === 'Active' ? '#D1FAE5' :
    props.status === 'Warning' ? '#FEF3C7' :
    props.status === 'Inactive' ? '#FEE2E2' :
    '#E5E7EB'
  };
  color: ${props => 
    props.status === 'Healthy' || props.status === 'Active' ? '#065F46' :
    props.status === 'Warning' ? '#92400E' :
    props.status === 'Inactive' ? '#991B1B' :
    '#374151'
  };
`;

const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 0.75rem;
  border-top: 1px solid #f3f4f6;
`;

const PaginationButtons = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const PageButton = styled.button`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  border: 1px solid ${props => props.active ? '#0066FF' : '#e5e7eb'};
  background: ${props => props.active ? '#0066FF' : 'white'};
  color: ${props => props.active ? 'white' : '#6b7280'};
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: ${props => props.active ? '#0052CC' : '#f9fafb'};
    border-color: ${props => props.active ? '#0052CC' : '#d1d5db'};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const ViewAllButton = styled.button`
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: #0066FF;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    color: #0052CC;
    text-decoration: underline;
  }
`;

const ArrowIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  color: #0066FF;
  margin-left: 0.25rem;
`;

const LoaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  gap: 1rem;
`;

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid #f3f4f6;
  border-top-color: #0066FF;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const LoaderText = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
  font-weight: 500;
`;

const PerformanceCard = ({
  title,
  graphData,
  performanceData = [],
  tabs,
  activeTab,
  onTabChange,
  onViewAll,
  type, // 'agents', 'teams', 'planners'
  isLoading = false
}) => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const handleItemClick = (item) => {
    // For runs/sessions tabs, navigate to details with database record id only
    if (activeTab === 'agent-sessions' || activeTab === 'teams-run' || activeTab === 'planner-run') {
      const metricId = item.id;
      if (metricId) {
        navigate(`/observability/details/${metricId}`);
      }
    } else {
      // For primary tabs (agents, teams, planners), navigate to details page with tab and selected ID
      // Use the appropriate ID field based on entity type
      let entityId = null;
      let tabName = '';
      
      if (type === 'agents') {
        tabName = 'agents';
        entityId = item.agent_id || item.id;
      } else if (type === 'teams') {
        tabName = 'teams';
        entityId = item.team_id || item.id;
      } else if (type === 'planners') {
        tabName = 'planners';
        entityId = item.planner_id || item.id;
      }
      
      if (entityId) {
        navigate(`/observability/details?tab=${tabName}&id=${entityId}`);
      }
    }
  };

  // Calculate pagination
  const totalPages = Math.ceil(performanceData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = performanceData.slice(startIndex, endIndex);

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;
    
    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= maxPagesToShow; i++) {
          pages.push(i);
        }
      } else if (currentPage >= totalPages - 2) {
        for (let i = totalPages - maxPagesToShow + 1; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        for (let i = currentPage - 2; i <= currentPage + 2; i++) {
          pages.push(i);
        }
      }
    }
    
    return pages;
  };

  const getHeaderLabels = () => {
    if (type === 'agents') {
      return {
        col1: 'Agents',
        col2: 'Success Rate',
        col3: 'Token Usage'
      };
    } else if (type === 'teams') {
      return {
        col1: 'Team ID / Planner ID',
        col2: 'Success Rate',
        col3: 'Token Usage'
      };
    } else if (type === 'planners') {
      return {
        col1: 'Planner ID/ Name',
        col2: 'Success Rate',
        col3: 'Token Usage'
      };
    }
    return {
      col1: 'Name',
      col2: 'Metric',
      col3: 'Value'
    };
  };

  const headers = getHeaderLabels();

  // Parse metric value to get percentage for progress bar
  const getMetricPercentage = (metric) => {
    if (typeof metric === 'string' && metric.includes('%')) {
      return parseInt(metric.replace('%', ''));
    }
    return null;
  };

  return (
    <CardContainer>
      <CardHeader>
        <CardTitle>{title} Performance</CardTitle>
        {tabs && tabs.length > 0 && (
          <TabGroup>
            {tabs.map((tab) => (
              <Tab
                key={tab.key}
                active={activeTab === tab.key}
                onClick={() => onTabChange(tab.key)}
              >
                {tab.label}
              </Tab>
            ))}
          </TabGroup>
        )}
      </CardHeader>

      <GraphContainer>
        <ReusableTrendsGraph
          labels={graphData.labels}
          totalData={graphData.totalData}
          failedData={graphData.failedData}
          totalLabel={graphData.totalLabel || "Total"}
          failedLabel={graphData.failedLabel || "Failed"}
          height="200px"
          maxValue={graphData.maxValue || 250}
          stepSize={graphData.stepSize || 50}
        />
      </GraphContainer>

      <PerformanceSection>
        {isLoading ? (
          <LoaderContainer>
            <Spinner />
            <LoaderText>Loading {title.toLowerCase()} data...</LoaderText>
          </LoaderContainer>
        ) : (
          <>
            <TableHeader>
              <HeaderCell>{headers.col1}</HeaderCell>
              <HeaderCell>{headers.col2}</HeaderCell>
              <HeaderCell>{headers.col3}</HeaderCell>
              <HeaderCell></HeaderCell>
            </TableHeader>

            <TableBody>
              {currentItems.map((item, index) => {
                const metricPercentage = getMetricPercentage(item.metric);
                
                return (
                  <TableRow key={index} onClick={() => handleItemClick(item)}>
                    <div style={{ minWidth: 0, overflow: 'hidden' }}>
                      <ItemName>{item.name}</ItemName>
                      <ItemSubtext>{item.subtext}</ItemSubtext>
                    </div>
                    
                    <MetricCell>
                      <MetricValue>{item.metric}</MetricValue>
                      {metricPercentage !== null && (
                        <ProgressBar>
                          <ProgressFill value={metricPercentage} />
                        </ProgressBar>
                      )}
                    </MetricCell>
                    
                    <div>
                      <StatusBadge status={item.metricStatus || item.status}>
                        {item.total_tokens ? item.total_tokens.toLocaleString() : "0"}
                      </StatusBadge>
                    </div>
                    
                    <ArrowIcon>›</ArrowIcon>
                  </TableRow>
                );
              })}
            </TableBody>

            <PaginationContainer>
              <PaginationButtons>
                {getPageNumbers().map((pageNum) => (
                  <PageButton
                    key={pageNum}
                    active={currentPage === pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                  >
                    {pageNum}
                  </PageButton>
                ))}
              </PaginationButtons>
              
              <ViewAllButton onClick={onViewAll}>
                View All
              </ViewAllButton>
            </PaginationContainer>
          </>
        )}
      </PerformanceSection>
    </CardContainer>
  );
};

export default PerformanceCard;
